import React, { useEffect, useState } from 'react';
import { styled, keyframes } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  DialogActions,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon,
  Circle as CircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useParams } from 'react-router-dom';
import { useAppLocale } from '../AppLocaleProvider';

const progress = keyframes`
from {
width: 0;
}
to {
width: 100%
}
`;

interface QcDisclaimerPopupProps {
  displayPopup: boolean;
}

export const QcDisclaimerPopup = ({
  displayPopup = false,
}: QcDisclaimerPopupProps) => {
  const intl = useIntl();
  const { lang } = useParams();
  const [locale] = useAppLocale();
  const [isQuebecPopupOpen, setQuebecPopupOpen] = useState<boolean>(false);

  useEffect(() => {
    setQuebecPopupOpen(displayPopup);
    console.log(lang);
  }, [displayPopup, lang]);

  const handleClosePopup = () => {
    setQuebecPopupOpen(false);
  };

  return (
    <Dialog open={isQuebecPopupOpen} onClose={handleClosePopup} maxWidth="lg">
      <DialogTitle
        sx={{ backgroundColor: '#1375aaff', padding: '10px', width: '100%' }}
      >
        <Typography variant="h6" component="span" sx={{ color: 'white' }}>
          <FormattedMessage
            id="components.QcDisclaimerPopup.header"
            defaultMessage="Important Information for Patients in Quebec"
          />
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClosePopup}
          sx={{
            position: 'absolute',
            right: 3,
            top: 3,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Box>
            <p>
              <FormattedMessage
                id="components.QcDisclaimerPopup.information.p1"
                defaultMessage="As of April 15, 2021, the Government of Quebec has implemented new
              regulations restricting financial assistance programs to patients
              for drugs covered by RAMQ reimbursement and which appear on the
              Quebec formulary."
              />
            </p>

            <p>
              <strong>
                <FormattedMessage
                  id="components.QcDisclaimerPopup.information.p2strong"
                  defaultMessage="If your medication has a generic available and is reimbursed by
                  the RAMQ"
                />
              </strong>
              <FormattedMessage
                id="components.QcDisclaimerPopup.information.p2"
                defaultMessage=", it is impacted by this regulation. However, if you are an RxHelp
              ONE program participant and you have received a payment or
              reimbursement on your current medication prior to April 15, you
              will continue to receive RxHelp ONE reimbursement for your
              medication. However, as of April 15, 2021, no new patients will be
              eligible to receive payment or reimbursement on RxHelp ONE
              medications for which a generic is listed on the Quebec formulary.
              Please note: All of these medications have been removed from the
              Quebec available programs below."
              />
            </p>
            <p>
              <strong>
                <FormattedMessage id="components.QcDisclaimerPopup.information.p3strong1" />
              </strong>
              <FormattedMessage id="components.QcDisclaimerPopup.information.p3regular1" />
              <strong>
                <FormattedMessage id="components.QcDisclaimerPopup.information.p3strong2" />
              </strong>
              <FormattedMessage id="components.QcDisclaimerPopup.information.p3regular2" />
            </p>
            <p style={{ fontStyle: 'italic', fontWeight: 'bold' }}>
              <FormattedMessage
                id="components.QcDisclaimerPopup.information.p4"
                defaultMessage="QUEBEC PATIENT REFERENCE CHART: An Act respecting prescription
              drug insurance (chapter A-29.01, s. 80.2, par. 1, including
              Newsletter 118 dated July 23, 2021)"
              />
            </p>

            <TableContainer component={Paper}>
              <Table style={{ fontStyle: 'italic' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="left"
                      sx={{ verticalAlign: 'top', width: 200 }}
                    >
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.header1"
                        defaultMessage="Type of patient"
                      />
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ verticalAlign: 'top', width: 550 }}
                    >
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.header2"
                        defaultMessage="A product where NO Generic or Biosimilar is listed with
                      RAMQ, or if the product is an original Biologic or
                      Biosimilar"
                      />
                    </TableCell>
                    <TableCell align="left" sx={{ verticalAlign: 'top' }}>
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.header3p1"
                        defaultMessage="A product where a Generic or Biosimilar is listed with
                      RAMQ"
                      />
                      <p>
                        <FormattedMessage
                          id="components.QcDisclaimerPopup.information.header3p2"
                          defaultMessage="prior to April 15, 2021"
                        />
                      </p>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell align="left">
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.row1col1"
                        defaultMessage="Existing patients"
                      />
                    </TableCell>
                    <TableCell align="left">
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.row1col2"
                        defaultMessage="Can be reimbursed"
                      />
                    </TableCell>
                    <TableCell align="left">
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.row1col3"
                        defaultMessage="Can continue to be reimbursed"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left">
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.row2col1"
                        defaultMessage="New"
                      />
                    </TableCell>
                    <TableCell align="left">
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.row2col2"
                        defaultMessage="Can be"
                      />
                    </TableCell>
                    <TableCell align="left" style={{ fontWeight: 'bold' }}>
                      <FormattedMessage
                        id="components.QcDisclaimerPopup.information.row2col3"
                        defaultMessage="Cannot be"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContentText>
      </DialogContent>
      <Divider sx={{ width: '100%', mb: 1 }} />
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          <Button
            onClick={handleClosePopup}
            color="primary"
            variant="contained"
            sx={{
              borderRadius: '8px',
              padding: '8px 16px',
              color: 'white',
              backgroundColor: '#1375aaff',
            }}
          >
            <FormattedMessage
              id="components.QcDisclaimerPopup.information.closeButton"
              defaultMessage="Close"
            />
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
